import React, { useState, useEffect } from 'react';
import { Plus, ArrowRight, Clock } from 'lucide-react';
import { transferService, bedService, wardService } from '../../../lib/services.ipd';
import type { AdmissionTransfer, Bed, Ward } from '../../../lib/services.ipd';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { supabase } from '../../../lib/supabase';

interface TransfersTabProps {
  admissionId: string;
}

export default function TransfersTab({ admissionId }: TransfersTabProps) {
  const [transfers, setTransfers] = useState<AdmissionTransfer[]>([]);
  const [beds, setBeds] = useState<Bed[]>([]);
  const [wards, setWards] = useState<Ward[]>([]);
  const [loading, setLoading] = useState(true);
  const [showTransferForm, setShowTransferForm] = useState(false);
  
  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm<{
    ward_id: string;
    bed_id: string;
    reason: string;
  }>();

  const selectedWard = watch('ward_id');

  useEffect(() => {
    loadData();
  }, [admissionId]);

  useEffect(() => {
    if (selectedWard) {
      loadBedsForWard(selectedWard);
    }
  }, [selectedWard]);

  const loadData = async () => {
    try {
      const [transfersData, wardsData, bedsData] = await Promise.all([
        transferService.getByAdmission(admissionId),
        wardService.getAll(),
        bedService.getAvailable(),
      ]);
      setTransfers(transfersData);
      setWards(wardsData);
      setBeds(bedsData);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load transfer data');
    } finally {
      setLoading(false);
    }
  };

  const loadBedsForWard = async (wardId: string) => {
    try {
      const { data, error } = await supabase
        .from('beds')
        .select('*, ward:wards(*)')
        .eq('ward_id', wardId)
        .eq('active', true)
        .eq('status', 'available');
      
      if (error) throw error;
      setBeds(data || []);
    } catch (error) {
      console.error('Failed to load beds:', error);
    }
  };

  const onSubmit = async (data: { ward_id: string; bed_id: string; reason: string }) => {
    try {
      await transferService.transferBed({
        admissionId,
        toBedId: data.bed_id,
        reason: data.reason,
      });
      toast.success('Patient transferred successfully');
      reset();
      setShowTransferForm(false);
      loadData();
    } catch (error) {
      console.error('Failed to transfer patient:', error);
      toast.error('Failed to transfer patient');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">Bed Transfer History</h3>
        <button
          onClick={() => {
            setShowTransferForm(!showTransferForm);
            reset();
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Transfer Bed
        </button>
      </div>

      {showTransferForm && (
        <form onSubmit={handleSubmit(onSubmit)} className="bg-gray-50 p-4 rounded-lg space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target Ward <span className="text-red-500">*</span>
            </label>
            <select
              {...register('ward_id', { required: 'Ward is required' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select ward</option>
              {wards.map((ward) => (
                <option key={ward.id} value={ward.id}>
                  {ward.name} ({ward.floor})
                </option>
              ))}
            </select>
            {errors.ward_id && <p className="text-sm text-red-600 mt-1">{errors.ward_id.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target Bed <span className="text-red-500">*</span>
            </label>
            <select
              {...register('bed_id', { required: 'Bed is required' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={!selectedWard}
            >
              <option value="">Select bed</option>
              {beds.map((bed) => (
                <option key={bed.id} value={bed.id}>
                  {bed.bed_no} - {bed.bed_type} (₹{bed.daily_rate}/day)
                </option>
              ))}
            </select>
            {errors.bed_id && <p className="text-sm text-red-600 mt-1">{errors.bed_id.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason for Transfer
            </label>
            <textarea
              {...register('reason')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Enter reason for transfer..."
            />
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Confirm Transfer
            </button>
            <button
              type="button"
              onClick={() => {
                setShowTransferForm(false);
                reset();
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {transfers.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No transfers recorded</p>
        ) : (
          transfers.map((transfer) => (
            <div key={transfer.id} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-600">
                    {new Date(transfer.transferred_at).toLocaleString()}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 text-gray-900">
                <span className="font-medium">{transfer.from_ward_name || 'Unassigned'}</span>
                <ArrowRight className="w-4 h-4 text-blue-600" />
                <span className="font-medium">{transfer.to_ward_name}</span>
              </div>
              {transfer.reason && (
                <p className="text-sm text-gray-600 mt-2">Reason: {transfer.reason}</p>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
